import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  User, CalendarPlus, CalendarDays, Images, Building2, CalendarRange,
  ChevronRight, Disc3, LogOut,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { fetchMyDjApplication, listMyDjEvents } from '@/lib/featuresApi';

// Premium split-screen DJ control center. Drives every status/headline/card from
// REAL data only: GET /users/me (name, avatar, role) and the safe DJ application
// view (status, public_profile_url, gallery_notice). No fake bookings, payments,
// followers, or analytics numbers are shown. Mirrors the auth pattern used by
// DJStatus / DJEvents (token-based api.isAuthenticated, not legacy localStorage).

// Status -> left-panel copy. Anything that is not approved/pending collapses to a
// safe "needs attention" state (covers rejected, removed, archived, null/unknown).
const STATUS_COPY = {
  approved: {
    badge: 'PROFILE APPROVED & LIVE',
    headline: 'YOUR DJ IS LIVE.',
    text: 'You’re verified and discoverable to fans, venues, and promoters across SoundWolves.',
    dot: 'bg-emerald-500',
    statLabel: 'Live',
  },
  pending: {
    badge: 'PROFILE UNDER REVIEW',
    headline: 'YOUR DJ PROFILE IS UNDER REVIEW.',
    text: 'Your application is being reviewed. We’ll update you once it is approved.',
    dot: 'bg-amber-500',
    statLabel: 'In review',
  },
  attention: {
    badge: 'PROFILE NEEDS ATTENTION',
    headline: 'YOUR DJ PROFILE NEEDS ATTENTION.',
    text: 'Your DJ profile is not active right now. You can reapply or update your details.',
    dot: 'bg-rose-500',
    statLabel: 'Inactive',
  },
};

const statusKey = (s) => (s === 'approved' ? 'approved' : s === 'pending' ? 'pending' : 'attention');

export default function DJDashboard() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [redirect, setRedirect] = useState(null);
  const [me, setMe] = useState(null);
  const [status, setStatus] = useState(null);
  const [profileUrl, setProfileUrl] = useState(null);
  const [galleryNotice, setGalleryNotice] = useState(null);
  const [eventCount, setEventCount] = useState(null);   // number when approved, else null

  useEffect(() => {
    if (!api.isAuthenticated()) { setRedirect('/login'); setLoading(false); return; }
    let active = true;
    (async () => {
      try {
        const [profile, app] = await Promise.all([
          api.get('/users/me').catch(() => null),
          fetchMyDjApplication().catch(() => ({ status: null })),
        ]);
        if (!active) return;
        setMe(profile);
        setStatus(app?.status || null);
        setProfileUrl(app?.public_profile_url || null);
        setGalleryNotice(app?.gallery_notice || null);
        // Real event count is only available to approved DJs (endpoint is gated).
        if (app?.status === 'approved') {
          const list = await listMyDjEvents().catch(() => []);
          if (active) setEventCount(Array.isArray(list) ? list.length : 0);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const handleLogout = async () => {
    try { await logout(); } catch (_) { /* logout best-effort */ }
    toast.success('Logged out');
    navigate('/');
  };

  if (redirect) return <Navigate to={redirect} replace />;
  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-24 flex items-center justify-center" data-testid="dj-dashboard-loading">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const copy = STATUS_COPY[statusKey(status)];
  const isApproved = status === 'approved';
  const isOrganizer = me && (me.role === 'organizer' || me.role === 'admin');
  const displayName = (me && (me.name || me.full_name || me.username)) || 'DJ';

  // Approved hero headline is personalized and ALL CAPS, e.g. "NOVA IS LIVE."
  // Fallback order: DJ stage name (from /auth/me) -> profile/display name -> "DJ"
  // (so with no name it reads "DJ IS LIVE."). Other states keep their shared
  // STATUS_COPY headline untouched.
  const heroName = ((user?.dj_name || displayName || 'DJ').trim() || 'DJ').toUpperCase();
  const headline = isApproved ? `${heroName} IS LIVE.` : copy.headline;

  // Real routes only. View DJ Profile -> live public page if a slug exists, else
  // the status page; gallery lives inside the DJ profile/application editor.
  const cards = [
    { icon: User, title: 'View DJ Profile', sub: 'See how fans view your public artist page', to: profileUrl || '/dj-status', highlight: true },
    { icon: CalendarPlus, title: 'Create Event', sub: 'Schedule your next performance', to: '/dj/events' },
    { icon: CalendarDays, title: 'My Events', sub: 'Manage and track your events', to: '/dj/events' },
    { icon: Images, title: 'Manage Gallery', sub: 'Add photos and videos to your DJ profile', to: '/dj-application' },
    ...(isOrganizer ? [
      { icon: Building2, title: 'Organizer Profile', sub: 'Manage your organizer identity', to: '/organizer/profile' },
      { icon: CalendarRange, title: 'Organizer Events', sub: 'Create and manage organizer events', to: '/organizer/events' },
    ] : []),
  ];

  const stats = [
    { label: 'Profile Status', value: copy.statLabel, hint: 'Your application state', dot: copy.dot },
    {
      label: 'Events',
      value: isApproved ? String(eventCount ?? 0) : '—',
      hint: isApproved ? 'Created by you' : 'Approval required',
    },
    { label: 'Gallery', value: 'Edit', hint: galleryNotice ? 'Review note on file' : 'Photos & video' },
  ];

  return (
    <div className="min-h-screen lg:flex" data-testid="dj-dashboard-page">
      {/* ---------------- LEFT: gold status panel ---------------- */}
      <aside
        className="relative overflow-hidden flex flex-col justify-between
                   px-6 sm:px-10 lg:px-12 pt-20 lg:pt-28 pb-10 lg:pb-12
                   lg:w-[43%] lg:min-h-screen text-zinc-900"
        style={{ background: 'linear-gradient(150deg, hsl(45 97% 63%) 0%, hsl(43 96% 55%) 52%, hsl(38 92% 47%) 100%)' }}
      >
        {/* decorative watermark */}
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -bottom-16 -right-6 font-display font-black leading-none text-black/10 text-[36vw] lg:text-[20rem]"
        >
          DJ
        </span>

        <div className="relative z-10">
          <p className="text-xs font-bold tracking-[0.3em] text-zinc-900/70">DJBOARD</p>

          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-black/15 bg-black/10 px-3 py-1">
            <span className={`w-2 h-2 rounded-full ${copy.dot}`} />
            <span className="text-[11px] font-bold tracking-wider uppercase" data-testid="dj-dashboard-badge">{copy.badge}</span>
          </div>

          <h1 className="mt-6 font-display font-black leading-[0.95] text-5xl sm:text-6xl lg:text-7xl" data-testid="dj-dashboard-headline">
            {headline}
          </h1>

          <p className="mt-5 max-w-md text-sm sm:text-base text-zinc-900/80 leading-relaxed">
            {copy.text}
          </p>
        </div>

        <div className="relative z-10 mt-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-zinc-900 text-primary flex items-center justify-center shadow-lg ring-2 ring-black/10">
            <Disc3 className="w-6 h-6" />
          </div>
          <div className="leading-tight">
            <p className="text-sm font-bold tracking-wide">SOUNDWOLVES</p>
            <p className="text-[11px] font-semibold tracking-widest text-zinc-900/60 uppercase">DJ Control Center</p>
          </div>
        </div>
      </aside>

      {/* ---------------- RIGHT: dark action panel ---------------- */}
      <main className="flex-1 bg-background px-6 sm:px-10 lg:px-12 pt-10 lg:pt-28 pb-28 md:pb-12">
        <div className="max-w-2xl mx-auto lg:mx-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold tracking-[0.3em] text-primary">WELCOME BACK</p>
              <h2 className="mt-2 text-3xl sm:text-4xl font-display font-bold">
                What would you like to do<span className="text-primary">?</span>
              </h2>
              <p className="mt-1 text-sm text-muted-foreground truncate">Signed in as {displayName}</p>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
              data-testid="dj-dashboard-logout"
            >
              <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>

          {/* Action cards */}
          <div className="mt-8 space-y-3" data-testid="dj-dashboard-cards">
            {cards.map((c) => {
              const Icon = c.icon;
              const base = 'group w-full text-left rounded-2xl p-4 sm:p-5 flex items-center gap-4 transition-all duration-200';
              const cls = c.highlight
                ? `${base} bg-primary text-primary-foreground shadow-[0_8px_32px_hsl(43_96%_56%_/_0.25)] hover:-translate-y-0.5`
                : `${base} bg-card border border-border hover:border-primary/50 hover:bg-muted/40 hover:-translate-y-0.5`;
              return (
                <button key={c.title} type="button" onClick={() => navigate(c.to)} className={cls}>
                  <span className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${c.highlight ? 'bg-black/15' : 'bg-primary/10 text-primary'}`}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display font-bold text-base sm:text-lg leading-tight">{c.title}</span>
                    <span className={`block text-sm truncate ${c.highlight ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>{c.sub}</span>
                  </span>
                  <ChevronRight className={`shrink-0 w-5 h-5 transition-transform group-hover:translate-x-1 ${c.highlight ? 'text-primary-foreground/80' : 'text-muted-foreground'}`} />
                </button>
              );
            })}
          </div>

          {/* Real status / stat cards (no fabricated numbers) */}
          <div className="mt-8 grid grid-cols-3 gap-3" data-testid="dj-dashboard-stats">
            {stats.map((s) => (
              <div key={s.label} className="rounded-2xl bg-card border border-border p-4">
                <p className="text-[11px] font-semibold tracking-wide uppercase text-muted-foreground">{s.label}</p>
                <div className="mt-2 flex items-center gap-2">
                  {s.dot && <span className={`w-2 h-2 rounded-full ${s.dot}`} />}
                  <p className="text-xl sm:text-2xl font-bold leading-none truncate">{s.value}</p>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground truncate">{s.hint}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
